import { get } from "lodash";

const getIsRequiredFieldEmpty = (fieldValue: unknown) =>
  fieldValue === undefined || fieldValue === null || fieldValue === "" || fieldValue === false;

// Kept in this order because a required field is matched to the first list whose name it contains.
const personalFinanceLists = ["customerAssets", "liabilities", "income", "expenses"] as const;

type PersonalFinanceList = (typeof personalFinanceLists)[number];

// The fields a broker fills in on a row. The type and the fields with a default value (share, buffer and shading)
// are left out, because the rows a customer starts with already have them set.
const personalFinanceItemFields: Record<PersonalFinanceList, readonly string[]> = {
  customerAssets: ["value", "description"],
  liabilities: ["outstandingAmount", "limit", "financier", "repaymentFrequency", "repaymentAmount", "description"],
  income: ["amount", "frequency"],
  expenses: ["amount", "frequency"],
};

const getIsPersonalFinanceList = (listName: string): listName is PersonalFinanceList =>
  personalFinanceLists.some((personalFinanceList) => personalFinanceList === listName);

/**
 * The first row of a personal finance list is always validated. Any other row is only validated once one of its
 * fields holds a value, so the untouched rows a customer starts with never count as missing required fields.
 */
const getShouldValidatePersonalFinanceItem = ({
  listName,
  item,
  index,
}: {
  listName: PersonalFinanceList;
  item: Record<string, unknown> | undefined;
  index: number;
}): boolean =>
  index === 0 || personalFinanceItemFields[listName].some((field) => !getIsRequiredFieldEmpty(item?.[field]));

/**
 * The index of the first validated row missing `fieldPath`, or -1 when none is. A list without rows is missing it on
 * the first row, as that row is always validated.
 */
const getMissingPersonalFinanceItemIndex = ({
  listName,
  items,
  fieldPath,
}: {
  listName: PersonalFinanceList;
  items: Array<Record<string, unknown>>;
  fieldPath: string;
}): number => {
  if (items.length === 0) {
    return 0;
  }

  return items.findIndex(
    (item, index) =>
      getIsRequiredFieldEmpty(get(item, fieldPath)) && getShouldValidatePersonalFinanceItem({ listName, item, index }),
  );
};

export {
  getIsRequiredFieldEmpty,
  getIsPersonalFinanceList,
  getMissingPersonalFinanceItemIndex,
  getShouldValidatePersonalFinanceItem,
  personalFinanceLists,
};
